import { VehicleCameraPairingResponse, CameraDeviceSegment } from '$Generated/api';
import { React, moment } from 'Imports';
import { useEffect, useMemo } from 'react';
import { RequestVideoHeader } from '../RequestVideoHeader';
import { Divider } from 'MaterialUIComponents';
import SingleSelect from '$Components/FilterComponents/Filters/SingleSelect';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { TimeInput } from './TimeInput';
import * as DateFormatter from '$Components/Shared/DateFormatter';
import OfflineCameraBanner from '../SelectDateStep/OfflineCameraBanner';
import { isEncompass } from '$Utilities/integrationPlatform';
import FootageSegments, { CameraSegmentFullData, FootageViewState } from './FootageSegments';

const styles = require('./TimeAndDurationStep.scss') as {
    content: string;
    titleDescriptionWrapper: string;
    title: string;
    description: string;
    offlineBanner: string;
    inputWrapperGeotab: string;
    inputWrapperEncompass: string;
    footageSegmentsWrapper: string;
    timeInputWrapper: string;
    timeErrorText: string;
};

type StartTime = { hour: number; minute: number; second: number; amPm: 'AM' | 'PM' };

interface ITimeAndDurationStepProps {
    pairing: VehicleCameraPairingResponse;
    selectedDate: Date;
    startTime: StartTime | undefined;
    selectedDuration: number | undefined;
    durationOptions: IDropdownItem[];
    footageViewState: FootageViewState;
    availableSegments: CameraDeviceSegment[];
    onStartTimeChange: (startTime: StartTime | undefined) => void;
    onDurationChange: (duration: number | undefined) => void;
    onFootageViewStateChange: (state: FootageViewState) => void;
    onAvailableSegmentsChange: (segments: CameraDeviceSegment[]) => void;
    onSegmentSelected: (segment: CameraSegmentFullData) => void;
    onValidationErrorChange?: (hasError: boolean) => void;
}

export const TimeAndDurationStep = (props: ITimeAndDurationStepProps) => {
    const { pairing, selectedDate } = props;

    const isOnline = pairing.cameraDevice?.onlineStatus?.toLowerCase() === 'online';

    const requestStartDate = useMemo(() => {
        if (!props.startTime) return undefined;
        let h = props.startTime.hour;
        if (props.startTime.amPm === 'PM' && h !== 12) h += 12;
        if (props.startTime.amPm === 'AM' && h === 12) h = 0;
        return moment(selectedDate).startOf('day').hour(h).minute(props.startTime.minute).second(props.startTime.second).toDate();
    }, [selectedDate, props.startTime]);

    const requestEndDate = useMemo(
        () =>
            requestStartDate !== undefined && props.selectedDuration !== undefined
                ? moment(requestStartDate).add(props.selectedDuration, 'seconds').toDate()
                : undefined,
        [requestStartDate, props.selectedDuration],
    );

    // Checks the request's END (start + duration), not just its start -- e.g. at 3:00, a 2-minute recall
    // starting at 2:59 ends at 3:01 and is just as invalid as one that starts in the future outright. Falls
    // back to the bare start when no duration is selected yet, so an obviously-future start is still caught
    // immediately. Entered in the user's own time zone (see the description below), so this is already an
    // absolute instant comparison -- no zone conversion needed, unlike the footage-zone-entered preview time.
    const effectiveEndDate = requestEndDate ?? requestStartDate;
    const isFutureTime = effectiveEndDate !== undefined && effectiveEndDate.getTime() > moment().valueOf();

    useEffect(() => {
        props.onValidationErrorChange?.(isFutureTime);
        return () => props.onValidationErrorChange?.(false);
    }, [isFutureTime]);

    const handleTimeChange = (hour?: number, minute?: number, second?: number, amPm?: 'AM' | 'PM') => {
        if (hour !== undefined && minute !== undefined && second !== undefined && amPm !== undefined) {
            props.onStartTimeChange({ hour, minute, second, amPm });
        } else {
            props.onStartTimeChange(undefined);
        }
    };

    const handleDurationChange = (item: IDropdownItem | null) => {
        if (item) {
            props.onDurationChange(item.value as number);
        }
    };

    return (
        <>
            <RequestVideoHeader
                pairing={pairing}
                selectedDate={selectedDate}
                requestStartDate={requestStartDate}
                requestEndDate={requestEndDate}
            />
            {!isOnline && (
                <div className={styles.offlineBanner}>
                    <OfflineCameraBanner />
                </div>
            )}
            <div className={styles.content}>
                <div className={isEncompass ? styles.inputWrapperEncompass : styles.inputWrapperGeotab}>
                    <div className={styles.titleDescriptionWrapper}>
                        <div className={styles.title}>Enter Start Time of Recall</div>
                        <div className={styles.description}>
                            Start time should be entered based on <i>your</i> time zone ({DateFormatter.getTimezoneString()}).
                        </div>
                    </div>

                    <div className={styles.timeInputWrapper}>
                        <TimeInput onChange={handleTimeChange} initialValue={props.startTime} />
                        {isFutureTime && <div className={styles.timeErrorText}>Time cannot be in the future.</div>}
                    </div>
                </div>

                <div>
                    <Divider />
                </div>

                <div className={styles.titleDescriptionWrapper}>
                    <div className={styles.title}>Select Duration</div>
                    <div className={styles.description}>Select the footage duration to recall from the Start Time above.</div>
                    <SingleSelect
                        hideLabel
                        label=""
                        disabled={props.durationOptions.length === 0}
                        isMobileView={false}
                        selectionChanged={handleDurationChange}
                        selectedValue={props.durationOptions.length > 0 ? props.selectedDuration : undefined}
                        items={props.durationOptions}
                        sort={false}
                        placeholder="Select duration"
                        inputHeight={isEncompass ? 36 : 40}
                    />
                </div>

                <div>
                    <Divider />
                </div>

                {isOnline && (
                    <div className={styles.footageSegmentsWrapper}>
                        <FootageSegments
                            date={props.selectedDate}
                            pairing={props.pairing}
                            viewState={props.footageViewState}
                            availableSegments={props.availableSegments}
                            onViewStateChange={props.onFootageViewStateChange}
                            onSegmentsChange={props.onAvailableSegmentsChange}
                            onSegmentSelected={props.onSegmentSelected}
                        />
                    </div>
                )}
            </div>
        </>
    );
};
